import { useFeatureGate } from '@atlassian/feature-gates-react';

import { OAuthConsentFeatureGate } from '../../FeatureGates/FeatureGates';
import { useToken } from '../../Flow/ConsentContextProvider';
import { classifyScopes } from '../MCP/domain/trelloScopes';

/**
 * Chooses between the two MCP consent implementations.
 *
 * `MainContent/MCP` is the default and is the flow as it ships today.
 * `MainContent/MCP-optional-sites` is the same screen rebuilt so that a site, a
 * Trello workspace and a Bitbucket workspace are each optional. The two are
 * complete, independent copies — each owns its own context provider, consent
 * screen, footer and submitted form parameters.
 *
 * Both seams resolve the flow through this one hook, so the provider holding the
 * selection and the parameters submitting it can never come from different
 * implementations.
 *
 * Retiring the gate, keeping this flow:
 *   1. delete the `hasTrello` guard below, so Trello consent moves here too
 *      (this flow already supports it — see McpConsentLayout and the Trello
 *      cases in this folder's suites);
 *   2. point `Flow/ConsentLayout` and `Flow/FlowParameters` straight at this
 *      folder's `McpConsentLayout` / `McpFlowParameters`;
 *   3. delete the two switch components, this hook, and `MainContent/MCP`.
 *
 * Retiring the gate, keeping the default flow: revert the two imports in
 * `Flow/ConsentLayout` and `Flow/FlowParameters` to `MainContent/MCP`, then
 * delete this whole folder and the gate.
 */
export const useMcpOptionalSitesFlow = (): boolean => {
  const optionalSitesEnabled = useFeatureGate(
    OAuthConsentFeatureGate.MCP_OPTIONAL_SITES,
  );
  const token = useToken();
  // Trello consent stays on the default flow for as long as the gate exists.
  // This flow does support Trello, so that removing the gate is a straight
  // swap — but that path is deliberately not rolled out here yet, and is
  // covered by unit tests rather than by traffic. Drop this guard to move
  // Trello over.
  const { hasTrello } = classifyScopes(token.scopes ?? []);
  return optionalSitesEnabled && !hasTrello;
};
